import { constants } from '../config'

const { catNames, allCharges } = constants

const N8N_TAB_ORDER = [
  catNames.AI_SP_MONTHLY,
  catNames.AI_BILLING,
  catNames.AI_TRACKING,
  catNames.AI_TICKET_SP
]

/** Short labels for Clients graphed badges */
export const N8N_TAB_SHORT_LABELS = {
  [catNames.AI_SP_MONTHLY]: '$/SP',
  [catNames.AI_BILLING]: 'Billing',
  [catNames.AI_TRACKING]: 'SP',
  [catNames.AI_TICKET_SP]: 'SP/Tix'
}

const asStringArray = (value) => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
}

/** Per-client AI tab access from QBO / Jira / zendeskTracking. */
export const buildN8nTabCapabilities = ({
  hasQbo = false,
  hasJira = false,
  zendeskTracking = false
} = {}) => ({
  [catNames.AI_SP_MONTHLY]: Boolean(hasQbo && hasJira && zendeskTracking),
  [catNames.AI_BILLING]: Boolean(hasQbo),
  [catNames.AI_TRACKING]: Boolean(hasJira),
  [catNames.AI_TICKET_SP]: Boolean(hasJira && zendeskTracking)
})

export const clientSupportsN8nTab = (capabilities, tab) => Boolean(capabilities?.[tab])

/** Tabs every selected client can use (intersection). Empty if none in common. */
export const intersectN8nTabs = (capabilityMaps = []) => {
  const maps = (Array.isArray(capabilityMaps) ? capabilityMaps : []).filter(Boolean)
  if (!maps.length) {
    return N8N_TAB_ORDER.reduce((acc, tab) => {
      acc[tab] = false
      return acc
    }, {})
  }

  return N8N_TAB_ORDER.reduce((acc, tab) => {
    acc[tab] = maps.every((caps) => Boolean(caps?.[tab]))
    return acc
  }, {})
}

/** First tab in UI order that is enabled; optional preferredTab if still valid. */
export const pickAvailableN8nTab = (enabledTabs = {}, preferredTab = null) => {
  if (preferredTab && enabledTabs?.[preferredTab]) return preferredTab
  const found = N8N_TAB_ORDER.find((tab) => enabledTabs?.[tab])
  return found || null
}

/**
 * When intersection is empty (incompatible multi-select), fall back to union
 * so the user can still pick a tab without unselecting clients.
 */
export const resolveEnabledN8nTabs = (capabilityMaps = []) => {
  const maps = (Array.isArray(capabilityMaps) ? capabilityMaps : []).filter(Boolean)
  const intersection = intersectN8nTabs(maps)
  if (N8N_TAB_ORDER.some((tab) => intersection[tab])) return intersection

  return N8N_TAB_ORDER.reduce((acc, tab) => {
    acc[tab] = maps.some((caps) => Boolean(caps?.[tab]))
    return acc
  }, {})
}

/**
 * Build runtime client catalog from GET /api/socr/clients/tree.
 * Keyed by label (same string keys charts/billing already use).
 */
export const buildSocrClientCatalog = (treeClients = []) => {
  const clientSettings = {}
  const projectAvailability = {}
  const clientOptions = {}
  const n8nTabCapabilities = {}
  const projectKeys = []

  const rows = Array.isArray(treeClients) ? treeClients : []

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return
    if (row.isActiveSupport === false) return

    const id = typeof row.id === 'string' ? row.id.trim() : ''
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    if (!label) return

    const jiraProjects = Array.isArray(row.jiraProjects)
      ? row.jiraProjects
        .map((p) => (typeof p?.jiraName === 'string' ? p.jiraName.trim() : ''))
        .filter(Boolean)
      : []

    const googleProjects = asStringArray(row.googleProjects)
    const qboMappings = asStringArray(row.qboCustomers)
    const zendeskTracking = Boolean(row.zendeskTracking)
    const hasQbo = qboMappings.length > 0
    const hasJira = jiraProjects.length > 0
    const tabs = buildN8nTabCapabilities({ hasQbo, hasJira, zendeskTracking })
    const hasAnyN8nTab = N8N_TAB_ORDER.some((tab) => tabs[tab])

    clientOptions[label] = label
    projectKeys.push(label)
    n8nTabCapabilities[label] = tabs

    clientSettings[label] = {
      jiraProjects,
      googleProjects,
      qboMappings,
      color: typeof row.color === 'string' && row.color.trim() ? row.color.trim() : '#6b7280',
      label,
      charges: [...allCharges],
      socrId: id || null,
      zendeskTracking,
      vulnTracking: Boolean(row.vulnTracking),
      upgradeTracking: Boolean(row.upgradeTracking)
    }

    projectAvailability[label] = {
      google: googleProjects.length > 0,
      // QBO-only → AI Billing; Jira-only → AI SP Tracking; neither → not on N8N list
      n8n: hasAnyN8nTab
    }
  })

  return {
    clientOptions,
    clientSettings,
    projectAvailability,
    n8nTabCapabilities,
    projectKeys
  }
}

export { N8N_TAB_ORDER }
